<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :columns="columns"
      :details="inMainPanel"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isAsideOpen="isAsideOpen"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :reportType="reportType"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="selectionItemText"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :setSelectedModelId="setSelectedModelId"
      :showBound="false"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :viewport="viewport"
    />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import RouterService from 'client/util/RouterService';

import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import ConfirmationModal from 'components/Modal/ModalComponents/ConfirmationModal.vue';
import EventManager from 'client/GrowthEngineering/EventManager';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import RadiusMFAStatusBadge from 'client/Radius/RadiusMFAStatusBadge.vue';
import RadiusPrimaryAuthenticationStatusBadge from './RadiusPrimaryAuthenticationStatusBadge.vue';

const { ItemTableConsts, ItemTypeConsts } = Constants;
const { columnTypes } = ItemTableConsts;
const { error, success } = Constants.default;

export default {
  name: 'RadiusItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
      required: false,
    },
  },

  data: () => {
    const data = {
      itemType: ItemTypeConsts.radiusServer,
      route: '#/radius',
      reportType: ReportTypes.userToRadius,
    };

    return data;
  },

  computed: {
    ...mapGetters('RadiusServerCollection', {
      selectedItems: 'getSelectedItems',
    }),

    columns() {
      return [
        {
          dataField: 'name',
          headingText: 'Name',
          sortBy: 'name',
        },
        {
          dataField: 'networkSourceIp',
          headingText: 'IP Address',
          sortBy: 'networkSourceIp',
        },
        {
          bodyComponent: RadiusPrimaryAuthenticationStatusBadge,
          headingText: 'Primary Authentication',
        },
        {
          bodyComponent: RadiusMFAStatusBadge,
          headingText: 'MFA Status',
          classNames: {
            body: [columnTypes.mfaStatusColumn, columnTypes.radiusMfaStatusColumn],
            heading: [columnTypes.mfaStatusColumn, columnTypes.radiusMfaStatusColumn],
          },
        },
      ];
    },

    serverNoun() {
      return (this.selectedItems.length > 1) ? 'servers' : 'server';
    },

    serverNounCap() {
      return (this.selectedItems.length > 1) ? 'Servers' : 'Server';
    },
  },

  methods: {
    ...mapActions('RadiusServerCollection', [
      'deleteItems',
    ]),

    handleBulkDelete() {
      const {
        deleteItems, notifyDeleteItems, serverNoun, serverNounCap, selectedItems,
      } = this;

      ModalEventBus.open({
        component: ConfirmationModal,
        componentProps: {
          bodyText: `Are you sure you would like to delete the selected RADIUS ${serverNoun}?`,
          continueText: `Delete ${serverNoun}`,
          handleContinue: () => deleteItems(selectedItems)
            .then(res => notifyDeleteItems(res))
            .finally(() => {
              ModalEventBus.close();
            }),
          headerText: `Delete ${serverNounCap}`,
          mainActionAttention: true,
        },
      });
    },

    handleMainAction() {
      RouterService.redirect({
        path: '#/radius/new',
        viewport: this.viewport,
        vueRouter: this.$router,
      });

      const { triggerEvent } = EventManager;
      triggerEvent(events.addNewRadiusServerButtonClicked);
    },

    notifyDeleteItems(res) {
      if (res.failedTasks && res.failedTasks.length > 0) {
        return notify(error, `The selected RADIUS ${this.serverNoun} could not be deleted.`);
      }

      return notify(success, `RADIUS ${this.serverNoun} successfully deleted.`);
    },
  },
};
</script>
